import { Grid, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { FormatNumberForKm } from '../../../components/FormatNumber/FormatNumber';
import { USER_TIMEZONE } from '../../../utility/Constant';
import { formatPhoneNumber } from '../../Common/FormatPhoneNumber';

dayjs.extend(utc);
dayjs.extend(timezone);

const body = { fontSize: '16px', lineHeight: '24px', letterSpacing: '0.15px' };
const w600 = { fontWeight: 600 };
const w500 = { fontWeight: 500 };
const w400 = { fontWeight: 400 };
const titleSx = { fontSize: '20px', fontWeight: 500, lineHeight: '24px', letterSpacing: '0.15px' };
const colStackSx = { alignItems: 'flex-start', gap: '4px', width: '100%' };

function getPhoneTypeKey(type) {
    if (type == null || type === '') return '';
    if (typeof type === 'string') return String(type).toLowerCase();
    return String(type?.key || '').toLowerCase();
}

/** @param {Record<string, unknown> | null | undefined} customer */
export function splitCustomerPhones(customer) {
    if (!customer) return { mobile: null, work: null };
    const pt = getPhoneTypeKey(customer.primaryPhoneNumberType) || 'mobile';
    const st = getPhoneTypeKey(customer.secondaryPhoneNumberType);
    const p = customer.phoneNumber;
    const s = customer.secondaryPhoneNumber;

    let mobile = null;
    let work = null;

    if (pt === 'work') {
        work = p || null;
        if (s && st !== 'work') mobile = s;
    } else {
        mobile = p || null;
        if (st === 'work' && s) work = s;
    }
    if (!work && st === 'work' && s) work = s;

    return { mobile, work };
}

function mileageDateSuffix(dateStr) {
    if (!dateStr) return '';
    const d = dayjs(dateStr).tz(USER_TIMEZONE);
    if (!d.isValid()) return '';
    return ` (${d.format('YYYY-MM-DD')})`;
}

function formatOdoReading(odoVal, mileageUnitStr) {
    if (odoVal == null || odoVal === '' || Number(odoVal) === 0) return '-';
    return `${FormatNumberForKm(odoVal)} ${mileageUnitStr || ''}`.trim();
}

/**
 * @param {{
 *   customer?: Record<string, unknown> | null;
 *   customerPO?: string | null;
 *   vehicle?: Record<string, unknown> | null;
 *   mileageUnit?: string;
 *   odoDroppedOff?: unknown;
 *   odoReturned?: unknown;
 *   mileageInDate?: string | null;
 *   mileageOutDate?: string | null;
 *   estimateSingleOdo?: unknown;
 *   estimateSingleOdoDate?: string | null;
 *   estimateMileageOnly?: boolean;
 * }} props
 */
const DetailPageHeaderInfoColumns = ({
    customer = null,
    customerPO = null,
    vehicle = null,
    mileageUnit = '',
    odoDroppedOff,
    odoReturned,
    documentType = 'invoice', // invoice | estimate | workOrder
    invoiceDate = null,
    estimateDate = null,
    workOrderDate = null,
    authorizedDate = null,
    mileageInDate = null,
    mileageOutDate = null,
    // completionDate = null,
    estimateSingleOdo,
    estimateSingleOdoDate = null,
    estimateMileageOnly = false,
}) => {
    const street = (customer?.streetAddress && String(customer.streetAddress).trim()) || '';
    const cityLine = [customer?.city?.city, customer?.province?.province, customer?.postalCode].filter(Boolean).join(', ');

    const { mobile, work } = splitCustomerPhones(customer);
    const email = customer?.email ? String(customer.email) : '';
    const taxVal =
        customer?.taxExemptionNumber || customer?.taxExemptNumber || customer?.gstExemptionNumber || customer?.gstNumber || customer?.taxExemptionId || '';

    const isEstimate = documentType === 'estimate';
    const isWorkOrder = documentType === 'workOrder';

    const primaryDateLabel = isWorkOrder ? 'Work Order Date:' : isEstimate ? 'Estimate Date:' : 'Invoice Date:';

    const droppedOffLabel = isEstimate ? 'Expiration Date:' : 'Dropped Off:';

    const completionLabel = isEstimate ? 'Completion Date:' : 'Work Completion:';

    const primaryDateValue = isWorkOrder ? workOrderDate : isEstimate ? estimateDate : invoiceDate;

    const year = vehicle?.year;
    const make = vehicle?.vehicleMake?.name;
    const model = vehicle?.vehicleModel?.name;
    const trim = vehicle?.vehicleModelTrim?.name;
    const vehicleTitle = [year, make, model, trim].filter(Boolean).join(' ');

    const plateText = vehicle?.licenseNumber && (vehicle?.provinceCode ? `${vehicle.provinceCode} ${vehicle.licenseNumber}` : String(vehicle.licenseNumber));

    return (
        <Grid container item justifyContent="space-between" spacing={{ xs: 2, md: 2 }} columnSpacing={{ md: 2 }}>
            <Grid item xs={12} md={'auto'} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                <Stack>
                    <Typography variant="h6" fontWeight={'500'}>
                        Dates
                    </Typography>
                    <Stack direction="row" spacing={2}>
                        <Typography variant="body1" fontWeight={500}>
                            {primaryDateLabel}
                        </Typography>
                        <Typography variant="body1" fontWeight={400} className="less-space">
                            {primaryDateValue ? dayjs(primaryDateValue).tz(USER_TIMEZONE).format('DD-MMM-YYYY') : '-'}
                        </Typography>
                    </Stack>

                    {authorizedDate && (
                        <Stack direction="row" spacing={2}>
                            <Typography variant="body1" fontWeight={500}>
                                Authorization Date:
                            </Typography>
                            <Typography variant="body1" fontWeight={400} className="less-space">
                                {authorizedDate ? dayjs(authorizedDate).tz(USER_TIMEZONE).format('DD-MMM-YYYY') : '-'}
                            </Typography>
                        </Stack>
                    )}

                    {/* For Invoice keep same order. A work order has only its own date. */}
                    {!isEstimate && !isWorkOrder && (
                        <>
                            <Stack direction="row" spacing={2}>
                                <Typography variant="body1" fontWeight={500}>
                                    {droppedOffLabel}
                                </Typography>
                                <Typography variant="body1" fontWeight={400} className="less-space">
                                    {mileageInDate ? dayjs(mileageInDate).tz(USER_TIMEZONE).format('DD-MMM-YYYY') : '-'}
                                </Typography>
                            </Stack>

                            <Stack direction="row" spacing={2} style={{ width: 'max-content' }}>
                                <Typography variant="body1" fontWeight={500}>
                                    {completionLabel}
                                </Typography>
                                <Typography variant="body1" fontWeight={400} className="less-space">
                                    {mileageOutDate ? dayjs(mileageOutDate).tz(USER_TIMEZONE).format('DD-MMM-YYYY') : '-'}
                                </Typography>
                            </Stack>
                        </>
                    )}

                    {/* For Estimate move Expiration Date to last */}
                    {isEstimate && (
                        <>
                            <Stack direction="row" spacing={2} style={{ width: 'max-content' }}>
                                <Typography variant="body1" fontWeight={500}>
                                    {completionLabel}
                                </Typography>
                                <Typography variant="body1" fontWeight={400} className="less-space">
                                    {mileageOutDate ? dayjs(mileageOutDate).tz(USER_TIMEZONE).format('DD-MMM-YYYY') : '-'}
                                </Typography>
                            </Stack>

                            <Stack direction="row" spacing={2}>
                                <Typography variant="body1" fontWeight={500}>
                                    {droppedOffLabel}
                                </Typography>
                                <Typography variant="body1" fontWeight={400} className="less-space">
                                    {mileageInDate ? dayjs(mileageInDate).tz(USER_TIMEZONE).format('DD-MMM-YYYY') : '-'}
                                </Typography>
                            </Stack>
                        </>
                    )}
                </Stack>
            </Grid>

            <Grid item xs={12} md={'auto'} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                <Stack>
                    <Typography variant="h6" fontWeight={500}>
                        Customer Information
                    </Typography>
                    <Typography variant="body1" fontWeight={400}>
                        {customer?.fullName}
                    </Typography>
                    {customer?.company && (
                        <Typography variant="body1" fontWeight={400} style={{ padding: '2px 0' }}>
                            {customer?.company}
                        </Typography>
                    )}
                    <Typography variant="body1" fontWeight={400}>
                        {formatPhoneNumber(customer?.phoneNumber)}
                    </Typography>
                    <Typography variant="body1" fontWeight={400}>
                        {customer?.email}
                    </Typography>

                    {customerPO ? (
                        <Typography variant="body1" fontWeight={400}>
                            PO #: {customerPO}
                        </Typography>
                    ) : (
                        ''
                    )}

                    {taxVal ? (
                        <Typography variant="body1" fontWeight={400}>
                            Tax Exemption: {taxVal}
                        </Typography>
                    ) : (
                        ''
                    )}
                </Stack>
            </Grid>

            <Grid item xs={12} md={'auto'} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                <Stack sx={colStackSx}>
                    <Typography sx={titleSx}>Vehicle Information</Typography>
                    {vehicleTitle ? <Typography sx={{ ...body, ...w400 }}>{vehicleTitle}</Typography> : null}

                    {vehicle?.vin ? (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                            <Typography component="span" sx={{ ...body, ...w500 }}>
                                VIN:
                            </Typography>
                            <Typography component="span" sx={{ ...body, ...w400 }}>
                                {String(vehicle.vin)}
                            </Typography>
                        </Stack>
                    ) : null}

                    {vehicle?.licenseNumber ? (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                            <Typography component="span" sx={{ ...body, ...w500 }}>
                                Licence Plate:
                            </Typography>
                            <Typography component="span" sx={{ ...body, ...w400 }}>
                                {plateText}
                            </Typography>
                        </Stack>
                    ) : null}

                    {vehicle?.fleetUnitNumber ? (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                            <Typography component="span" sx={{ ...body, ...w500 }}>
                                Unit #:
                            </Typography>
                            <Typography component="span" sx={{ ...body, ...w400 }}>
                                {String(vehicle.fleetUnitNumber)}
                            </Typography>
                        </Stack>
                    ) : null}

                    {estimateMileageOnly ? (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" alignItems="baseline">
                            <Typography component="span" sx={{ ...body, ...w500 }}>
                                Mileage:
                            </Typography>
                            <Typography component="span" sx={{ ...body, ...w400 }}>
                                {formatOdoReading(estimateSingleOdo, mileageUnit)}
                                {/* {mileageDateSuffix(estimateSingleOdoDate)} */}
                            </Typography>
                        </Stack>
                    ) : (
                        <>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" alignItems="baseline">
                                <Typography component="span" sx={{ ...body, ...w500 }}>
                                    Mileage In:
                                </Typography>
                                <Typography component="span" sx={{ ...body, ...w400 }}>
                                    {formatOdoReading(odoDroppedOff, mileageUnit)}
                                    {/* {mileageDateSuffix(mileageInDate)} */}
                                </Typography>
                            </Stack>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" alignItems="baseline">
                                <Typography component="span" sx={{ ...body, ...w500 }}>
                                    Mileage Out:
                                </Typography>
                                <Typography component="span" sx={{ ...body, ...w400 }}>
                                    {formatOdoReading(odoReturned, mileageUnit)}
                                    {/* {mileageDateSuffix(mileageOutDate)} */}
                                </Typography>
                            </Stack>
                        </>
                    )}
                </Stack>
            </Grid>
        </Grid>
    );
};

export default DetailPageHeaderInfoColumns;
